import "~/styles/globals.scss";
import "~/styles/swiper-overrides.scss";
import "~/styles/toast.scss";

import type { AppProps } from "next/app";
import { Layout } from "~/layouts/layout";
import { CartProvider } from "~/context/cart";
import Head from "next/head";
import { BannerScript } from "~/utils/cookieBot";
import { Provider } from "jotai";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StoreValidation } from "./StoreValidation";
import { createPortal } from "react-dom";
import { useIsClient } from "usehooks-ts";
import { GoogleAnalytics } from "@next/third-parties/google";

const queryClient = new QueryClient();

export default function App({ Component, pageProps }: AppProps) {
  const isClient = useIsClient();

  return (
    <Provider>
      <QueryClientProvider client={queryClient}>
        <StoreValidation>
          <CartProvider>
            <Head>
              <title>Butik Danmark</title>
              <meta
                name="description"
                content="Butik Danmark er en gratis platform for små lokale producenter. Vis og sælg dine produkter, eller find og køb unikke danske kvalitetsvarer direkte fra producenten."
              />
              <meta property="og:locale" content="da_DK" />
              <meta property="og:type" content="website" />
              <meta property="og:site_name" content="Butik Danmark" />
              <meta property="og:title" content="Butik Danmark" />
              <meta
                property="og:description"
                content="Butik Danmark er en gratis platform for små lokale producenter. Vis og sælg dine produkter, eller find og køb unikke danske kvalitetsvarer direkte fra producenten."
              />
              <meta property="og:url" content="https://butikdanmark.dk" />
              <meta
                property="og:image"
                content="https://butikdanmark.dk/og-image.png"
              />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:site" content="@butikdanmark" />
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1"
              />
              {process.env.NODE_ENV === "production" ? (
                <link rel="icon" href="/favicon.png" />
              ) : (
                <link rel="icon" href="/faviconDev.png" />
              )}
            </Head>
            <BannerScript />
            {isClient &&
              createPortal(
                <Toaster position="bottom-right" reverseOrder={false} />,
                document.body
              )}
            <Layout>
              <Component {...pageProps} />
              {process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID ? (
                <GoogleAnalytics
                  gaId={process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}
                />
              ) : null}
            </Layout>
          </CartProvider>
        </StoreValidation>
      </QueryClientProvider>
    </Provider>
  );
}
